<template>
  <div>
    <CRow>
      <CCol lg="12">
        <h1 class="main-header" v-translate translate-context="chargeback.list">
          Disputes
        </h1>

        <CCard>
          <CCardHeader class="border-0 ml-3">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipAlertTriangle" size="custom-size" :width="18" />
              <span class="mx-1" v-translate translate-context="chargeback.list">All disputes</span>
              <span>({{ chargebacks.pagination.total|number }})</span>
            </div>

            <div class="card-header-actions">
              <CLink
                  v-if="canExport"
                  @click="onExport"
                  :disabled="!chargebacks.results || chargebacks.results.length <= 0 || exporting"
                  class="font-weight-medium d-flex align-items-center mr-3"
              >
                <CIcon width="16" size="custom-size" name="cipDownload03" class="mr-2"/>
                <translate translate-context="chargeback.list">Download the detailed report (.xls)</translate>
              </CLink>
              <CButton
                  v-if="canCreate"
                  color="primary"
                  @click="openAddModal"
              >
                <CIcon name="cil-plus" class="mr-1"/>
                <translate translate-context="chargeback.list">Add Dispute</translate>
              </CButton>
            </div>
          </CCardHeader>

          <CCardBody class="px-0">
            <AjaxTable
                v-if="isReady"
                ref="ajaxTable"
                :fields="fields"
                :data="chargebacks"
                :fetch="fetchList"
                table-name="chargebacks"
                :context-key="contextKey"
                :sync-url="true"
                class="custom-table custom-table--clean custom-table--has-calendar mx-4"
                :class="{'custom-table--clickable': canViewDetail}"
                @row-clicked="onRowClick"
            >
                <template #order_number="{item}">
                  <td>
                    <div>
                      <span class="font-weight-bold" v-if="item.order_number">
                        {{ item.order_number }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </td>
                </template>

                <template #event_name="{item}">
                  <td>
                    <router-link
                      v-if="item.pica_service_name && item.pica_service_id"
                      :to="{ name: 'picaServiceRedirect', params: { picaServiceId: item.pica_service_id } }"
                      @click.native.stop
                    >
                      {{ item.pica_service_name }}
                    </router-link>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #user_email="{item}">
                  <td>
                    <span v-if="item.user_email">{{ item.user_email }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #total_loss="{item}">
                  <td>
                    <strong class="text-danger">{{ item.total_loss|currency('EUR') }}</strong>
                  </td>
                </template>

                <template #photographer_loss="{item}">
                  <td>
                    <span v-if="item.photographer_loss != null">{{ item.photographer_loss|currency('EUR') }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #pica_loss="{item}">
                  <td>
                    <span v-if="item.pica_loss != null">{{ item.pica_loss|currency('EUR') }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #chargeback_type="{item}">
                  <td>
                    <CBadge v-if="item.chargeback_type" color="primary" class="badge-outline">
                      {{ item.chargeback_type.name }}
                    </CBadge>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #dispute_status="{item}">
                  <td>
                    <CBadge v-if="item.dispute_status" :color="getDisputeStatusColor(item.dispute_status)" class="badge-outline">
                      {{ getDisputeStatusLabel(item.dispute_status) }}
                    </CBadge>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

                <template #dispute_resolved_at="{item}">
                  <td>
                    <span v-if="item.dispute_resolved_at">{{ item.dispute_resolved_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </td>
                </template>

              </AjaxTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

    <AddChargebackModal
        v-if="canCreate"
        ref="addModal"
        @created="onChargebackCreated"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import AddChargebackModal from './components/AddChargebackModal.vue'
import { getPermissionPrefix } from '../constants'
import { DISPUTE_STATUSES, DISPUTE_STATUS_LABELS, DISPUTE_STATUS_COLORS } from './consts'
import ConfigScope from "@/domain/core/constant/configScope"

export default {
  name: 'ChargebackList',
  components: {
    AjaxTable,
    AddChargebackModal,
  },
  data() {
    return {
      isReady: false,
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await Promise.all([
      this.fetchScopedConfig(),
      this.fetchChargebackTypes(),
    ])
    this.isReady = true
  },
  computed: {
    ...mapGetters('accounting/chargeback', ['chargebacks', 'exporting']),
    ...mapGetters('core', ['chargebackTypes']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    contextKey() {
      if (this.scopeType === 'global') return 'global'
      return `${this.scopeType}-${this.scopeId}`
    },

    permissionPrefix() {
      return getPermissionPrefix(this.scopeType)
    },

    canCreate() {
      return this.scopeType === 'global' && this.hasPerm(`${this.permissionPrefix}.accounting.chargeback.create`)
    },

    canExport() {
      return this.scopeType === 'global' && this.hasPerm(`${this.permissionPrefix}.accounting.chargeback.export`)
    },

    canViewDetail() {
      return this.hasScopePerm('accounting.chargeback.detail')
    },

    fields() {
      const cols = [
        {
          key: 'order_number',
          label: this.$pgettext('chargeback.list', 'Order'),
          _style: 'width: 140px;',
          sorter: false,
        },
        {
          key: 'event_name',
          label: this.$pgettext('chargeback.list', 'Event'),
          sorter: false,
        },
      ]

      if (this.hasScopePerm('accounting.chargeback.fields.user_email')) {
        cols.push({
          key: 'user_email',
          label: this.$pgettext('chargeback.list', 'User'),
          _style: 'width: 200px;',
          sorter: false,
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.total_loss')) {
        cols.push({
          key: 'total_loss',
          label: this.$pgettext('chargeback.list', 'Total Loss'),
          _style: 'width: 130px;',
          sorter: true,
          filter: false,
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.photographer_loss')) {
        cols.push({
          key: 'photographer_loss',
          label: this.$pgettext('chargeback.list', 'Photographer Loss'),
          _style: 'width: 140px;',
          sorter: false,
          filter: false,
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.pica_loss')) {
        cols.push({
          key: 'pica_loss',
          label: this.$pgettext('chargeback.list', 'Pica Loss'),
          _style: 'width: 120px;',
          sorter: false,
          filter: false,
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.chargeback_type')) {
        cols.push({
          key: 'chargeback_type',
          label: this.$pgettext('chargeback.list', 'Dispute Type'),
          _style: 'width: 140px;',
          sorter: false,
          options: [
            { value: '', label: this.$gettext('Any') },
            ...this.chargebackTypes.map(t => ({ value: t.id, label: t.label })),
          ],
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.dispute_status')) {
        cols.push({
          key: 'dispute_status',
          label: this.$pgettext('chargeback.list', 'Dispute Status'),
          _style: 'width: 160px;',
          sorter: false,
          defaultValue: DISPUTE_STATUSES.LOST,
          options: [
            { value: '', label: this.$gettext('Any') },
            ...Object.values(DISPUTE_STATUSES).map(value => ({
              value,
              label: DISPUTE_STATUS_LABELS[value] || value,
            })),
          ],
        })
      }

      if (this.hasScopePerm('accounting.chargeback.fields.dispute_resolved_at')) {
        cols.push({
          key: 'dispute_resolved_at',
          label: this.$pgettext('chargeback.list', 'Dispute Resolved'),
          _style: 'width: 150px;',
          sorter: true,
          filter: false,
        })
      }

      return cols
    },

    detailRouteName() {
      if (this.scopeType === 'contract-manager') return 'contractManager.chargebacks.detail'
      if (this.scopeType === 'pica-service') return 'event.chargebacks.detail'
      return 'admin.chargebacks.detail'
    },
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/chargeback', [
      'fetchChargebacks',
      'exportChargebacks',
    ]),
    ...mapActions('login', ['fetchConfig']),
    ...mapActions('core', ['fetchChargebackTypes']),

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    async fetchList({ filters, pagination, sorting }) {
      const mappedSorting = { ...sorting }
      if (mappedSorting.column === 'total_loss') {
        mappedSorting.column = 'chargeback_amount'
      }

      await this.fetchChargebacks({ filters, pagination, sorting: mappedSorting })
    },

    onRowClick(item) {
      if (!this.canViewDetail) return
      const params = { id: item.id }
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({ name: this.detailRouteName, params })
    },

    openAddModal() {
      this.$refs.addModal.open()
    },

    async onChargebackCreated() {
      await this.$refs.ajaxTable?.fetchList()
    },

    async onExport() {
      const filters = this.$refs.ajaxTable
        ? this.$refs.ajaxTable.getFilters()
        : {}

      if (!filters.date_from) {
        const oneYearAgo = new Date()
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
        filters.date_from = oneYearAgo.toISOString().split('T')[0]
      }
      if (!filters.date_to) {
        filters.date_to = new Date().toISOString().split('T')[0]
      }

      await this.exportChargebacks(filters)
    },
    getDisputeStatusLabel(status) {
      return DISPUTE_STATUS_LABELS[status] || status
    },

    getDisputeStatusColor(status) {
      return DISPUTE_STATUS_COLORS[status] || 'secondary'
    },
  },
}
</script>

<style scoped>
.custom-table--clickable >>> tbody tr {
  cursor: pointer;
}
.custom-table--clickable >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
